// =============================================================================
// lektorCore — czysty rdzeń toru głosowego Universe (bramka audio + lektor).
//
// Jedna implementacja dla Kina Genesis, Akademii partnera i Akademii
// użytkownika. Do 13.09.2026 tor głosowy żył w DWÓCH miejscach: Akademia
// (`AkademiaPage.tsx`) miała bramkę, lektora z pamięcią podręczną i test
// mikrofonu, a Kino Genesis (`GenesisCinema.speakLine`) miało własną syntezę
// bez pamięci i BEZ testu głośników — właściciel Jan-Dar wszedł do Genesis
// z wyciszonym stanowiskiem i prezentacja modułów przeleciała bez słowa,
// bo nikt nie zapytał go o dźwięk, zanim Nexus zaczął mówić. Wymóg
// właściciela: „na samym początku Genesis ma być sprawdzenie głośników
// i mikrofonu".
//
// Tu mieszka wyłącznie logika bez przeglądarki: teksty testu, werdykt
// „czy słyszę", tekst lektora ze slajdu, klucz pamięci bramki, skrót tekstu
// pod pamięć podręczną audio i porządek tej pamięci w sessionStorage.
// Mikrofon, Audio i fetch zostają w `useLektor` i `BramkaAudio`.
// =============================================================================

export interface SlajdLektora {
  tytul: string;
  punkty: string[];
}

/** Co Nexus mówi na początku testu rozmowy (tor: głośniki). */
export const POWITANIE_TESTU =
  'Cześć! Zanim zaczniemy, sprawdźmy, czy się słyszymy. ' + 'Po sygnale powiedz proszę: zaczynamy.';

/** Co Nexus mówi, gdy usłyszał człowieka (test zaliczony). */
export const POTWIERDZENIE_TESTU = 'Słyszę Cię świetnie. Zaczynamy!';

/**
 * Ile milisekund bramka nagrywa odpowiedź. Stała, nie wykrywanie ciszy:
 * test ma jedno krótkie zdanie do złapania, a wykrywanie ciszy na
 * niesprawnym mikrofonie (dokładnie ten przypadek, którego szukamy) nigdy
 * by się nie skończyło.
 */
export const NAGRANIE_TESTU_MS = 4000;

/** Ile milisekund nagrywa pytanie zadane mikrofonem w prezentacji. */
export const NAGRANIE_PYTANIA_MS = 7000;

/**
 * Sufit tekstu lektora na jedną wypowiedź. TTS liczy się od znaku, a slajd
 * ma być zdaniem wstępu i kilkoma punktami — dłuższy tekst to błąd treści,
 * nie powód do dłuższego rachunku.
 */
export const SUFIT_LEKTORA_ZNAKOW = 1500;

/**
 * Klucz sessionStorage z decyzją bramki („glos" / „cisza"). JEDEN dla
 * Genesis i obu Akademii: człowiek, który przed chwilą sprawdził głośniki
 * w Kinie, nie ma ich sprawdzać drugi raz w Akademii tej samej sesji.
 */
export const KLUCZ_BRAMKI = 'universe_bramka_audio';

/** Klucz sessionStorage z pamięcią podręczną zsyntezowanych wypowiedzi. */
export const KLUCZ_PAMIECI_LEKTORA = 'universe_lektor_pamiec';

/**
 * Sufit pamięci podręcznej audio w sessionStorage (bajty JSON-a). Minuta
 * mowy mp3 w base64 to ~600 KB, więc 2 MB mieści kilka scen — dość, żeby
 * „Poprzedni" i odświeżenie strony nie płaciły drugi raz, a za mało, żeby
 * zapchać limit przeglądarki (5 MB na origin) tak, że inne moduły tracą
 * swój zapis.
 */
export const LIMIT_PAMIECI_LEKTORA_BAJTOW = 2 * 1024 * 1024;

export type WyborBramki = 'glos' | 'cisza';

export interface StanBramki {
  /** `true` = bramka już rozstrzygnięta w tej sesji (nie pytaj drugi raz). */
  zaliczona: boolean;
  /** `true` = człowiek chce słyszeć lektora. */
  lektor: boolean;
}

/** Minimalny kształt magazynu — sessionStorage albo atrapa w teście. */
export interface MagazynKluczy {
  getItem(klucz: string): string | null;
  setItem(klucz: string, wartosc: string): void;
}

function magazynSesji(): MagazynKluczy | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    // sessionStorage bywa zablokowany (tryb prywatny, polityka) — bramka
    // po prostu zapyta ponownie, a lektor będzie syntezować bez pamięci.
    return null;
  }
}

/** Co sesja pamięta o bramce audio. */
export function zapamietanaBramka(magazyn: MagazynKluczy | null = magazynSesji()): StanBramki {
  try {
    const zapis = magazyn?.getItem(KLUCZ_BRAMKI);
    if (zapis === 'glos') return { zaliczona: true, lektor: true };
    if (zapis === 'cisza') return { zaliczona: true, lektor: false };
  } catch {
    /* jak wyżej */
  }
  return { zaliczona: false, lektor: false };
}

export function zapamietajBramke(
  wybor: WyborBramki,
  magazyn: MagazynKluczy | null = magazynSesji()
): void {
  try {
    magazyn?.setItem(KLUCZ_BRAMKI, wybor);
  } catch {
    /* jak wyżej */
  }
}

/**
 * Werdykt testu rozmowy: czy transkrypcja dowodzi, że mikrofon słyszał
 * człowieka. Nie porównujemy ze słowem „zaczynamy" — STT bywa twórcze,
 * a człowiek mówi, jak chce; wystarczy niepusty tekst z co najmniej jednym
 * słowem z liter (sama interpunkcja i pojedynczy znak to szum, nie mowa).
 */
export function czySlyszy(transkrypcja: string | null | undefined): boolean {
  if (!transkrypcja) return false;
  const slowa = transkrypcja
    .split(/\s+/)
    .map((s) => s.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter((s) => s.length >= 2);
  return slowa.length >= 1;
}

/**
 * Tekst, który lektor czyta dla slajdu: tytuł jako zdanie, potem punkty —
 * każdy domknięty kropką (punkty na slajdzie zwykle jej nie mają, a bez niej
 * TTS skleja dwa punkty w jedno zdanie bez oddechu). Sufit znaków ucina
 * uczciwie na granicy punktu, nie w pół słowa.
 */
export function tekstLektora(slajd: SlajdLektora): string {
  return zlozZdania([slajd.tytul, ...slajd.punkty]);
}

/** Składa zdania w jedną wypowiedź lektora, z domknięciem i sufitem znaków. */
export function zlozZdania(fragmenty: ReadonlyArray<string | null | undefined>): string {
  const zdania: string[] = [];
  for (const fragment of fragmenty) {
    const f = (fragment ?? '').trim();
    if (f) zdania.push(domknij(f));
  }
  let tekst = '';
  for (const zdanie of zdania) {
    const nastepny = tekst ? `${tekst} ${zdanie}` : zdanie;
    if (nastepny.length > SUFIT_LEKTORA_ZNAKOW) break;
    tekst = nastepny;
  }
  return tekst;
}

function domknij(zdanie: string): string {
  return /[.!?…:]$/.test(zdanie) ? zdanie : `${zdanie}.`;
}

/**
 * Skrót tekstu pod klucz pamięci podręcznej — FNV-1a 32 bit liczony dwa
 * razy (dwa ziarna), szesnastkowo. Nie kryptografia: chodzi o to, żeby
 * zmieniony tekst pod tym samym kluczem sceny NIE dostał starego nagrania.
 */
export function skrotTekstu(tekst: string): string {
  const fnv = (ziarno: number): string => {
    let h = ziarno >>> 0;
    for (let i = 0; i < tekst.length; i++) {
      h ^= tekst.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
  };
  return `${fnv(0x811c9dc5)}${fnv(0x050c5d1f)}`;
}

/** Klucz wpisu pamięci: nazwa sceny + skrót treści i głosu. */
export function kluczMowy(klucz: string, tekst: string, voice?: string): string {
  return `${klucz}#${skrotTekstu(`${voice ?? ''}|${tekst}`)}`;
}

/** Odczyt pamięci podręcznej audio z magazynu; uszkodzony zapis = pusta mapa. */
export function odczytajPamiecLektora(
  magazyn: MagazynKluczy | null = magazynSesji()
): Map<string, string> {
  try {
    const zapis = magazyn?.getItem(KLUCZ_PAMIECI_LEKTORA);
    if (!zapis) return new Map();
    const dane = JSON.parse(zapis) as unknown;
    if (!Array.isArray(dane)) return new Map();
    const mapa = new Map<string, string>();
    for (const wpis of dane) {
      if (Array.isArray(wpis) && typeof wpis[0] === 'string' && typeof wpis[1] === 'string') {
        mapa.set(wpis[0], wpis[1]);
      }
    }
    return mapa;
  } catch {
    return new Map();
  }
}

/**
 * Przycina pamięć do limitu, wyrzucając NAJSTARSZE wpisy (Map zachowuje
 * kolejność wstawiania). Zwraca nową mapę — czysta funkcja pod test.
 */
export function przytnijPamiecLektora(
  pamiec: Map<string, string>,
  limitBajtow: number = LIMIT_PAMIECI_LEKTORA_BAJTOW
): Map<string, string> {
  const wpisy = [...pamiec.entries()];
  const rozmiar = (w: [string, string]) => w[0].length + w[1].length + 8;
  let suma = wpisy.reduce((s, w) => s + rozmiar(w), 0);
  while (wpisy.length > 0 && suma > limitBajtow) {
    const najstarszy = wpisy.shift()!;
    suma -= rozmiar(najstarszy);
  }
  return new Map(wpisy);
}

/** Zapis pamięci do magazynu po przycięciu; brak magazynu = brak zapisu. */
export function zapiszPamiecLektora(
  pamiec: Map<string, string>,
  magazyn: MagazynKluczy | null = magazynSesji()
): Map<string, string> {
  const przycieta = przytnijPamiecLektora(pamiec);
  try {
    magazyn?.setItem(KLUCZ_PAMIECI_LEKTORA, JSON.stringify([...przycieta.entries()]));
  } catch {
    // Przepełniony magazyn (inne moduły) — pamięć w ref działa dalej.
  }
  return przycieta;
}

/** Typ MIME nagrania z serwera syntezy (domyślnie mp3). */
export function mimeMowy(format: string | undefined): string {
  return !format || format === 'mp3' ? 'audio/mpeg' : `audio/${format}`;
}
